import { useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import { toast } from 'sonner';

import { useSelector } from 'react-redux';
import {
  selectIsAdmin,
  selectUserToken,
} from '../../../../redux/features/user/userSelectors';
import {
  useGetReverseAddressQuery,
  usePinRealtyMutation,
} from '../../../../redux/services/realties/realtiesApi';
import {
  streetsApi,
  useAddStreetMutation,
  useDeleteStreetMutation,
} from '../../../../redux/services/data/streetsApi';
import {
  streetsNumberApi,
  useAddStreetNumberMutation,
  useDeleteStreetNumberMutation,
} from '../../../../redux/services/data/streetsNumberApi';

import { Dialog } from '../../Dialog';
import { RealtyPinPicker } from '../RealtyPinPicker';
import { AddRealtyForm } from '../../../form/forms/realtyForm/AddRealtyForm';

const formatAddress = ({ housenumber, street, postcode, city }) =>
  [
    [housenumber, street].filter(Boolean).join(' '),
    [postcode, city].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');

const realtyTitle = realty =>
  [
    `#${realty.id}`,
    realty.main?.property_name,
    realty.location?.location_city_id?.name,
  ]
    .filter(Boolean)
    .join(' · ');

const buildPrefill = (match, suggestion) => {
  if (!match?.city_id) return null;

  const location = {
    location_city_id: { id: match.city_id, name: suggestion.city },
  };

  if (match.street_id) {
    location.location_street_id = {
      id: match.street_id,
      name: suggestion.street,
    };
  }

  if (match.street_number_id) {
    location.location_street_number_id = {
      id: match.street_number_id,
      name: suggestion.housenumber,
    };
  }

  return { location };
};

const matchSummary = match =>
  [
    ['city', match?.city_id],
    ['street', match?.street_id],
    ['number', match?.street_number_id],
  ]
    .map(([label, id]) => `${label} ${id ? '✓' : '—'}`)
    .join(' · ');

const missingAddressParts = (match, suggestion) => {
  if (!match?.city_id || !suggestion) return null;

  const needsStreet = !match.street_id && Boolean(suggestion.street);
  const needsNumber =
    !match.street_number_id &&
    Boolean(suggestion.housenumber) &&
    (needsStreet || Boolean(match.street_id));

  if (!needsStreet && !needsNumber) return null;

  return {
    needsStreet,
    needsNumber,
    label: [suggestion.housenumber, suggestion.street]
      .filter(Boolean)
      .join(' '),
  };
};

const createdId = response => response?.data?.id ?? response?.id;

// accent / case-insensitive, so "Rue du Marechal Foch" finds "Rue du Maréchal Foch"
const normalizeName = value =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

const findIdByName = (items, name) =>
  items.find(item => normalizeName(item.name) === normalizeName(name))?.id;

export const PinDropDialog = ({
  pin,
  isOpen,
  onClose,
  onRemovePin,
  onSaved,
}) => {
  const userToken = useSelector(selectUserToken);
  const isAdmin = useSelector(selectIsAdmin);
  const [pickedRealty, setPickedRealty] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const createdRealtyIdRef = useRef(null);
  const [shouldCreateAddress, setShouldCreateAddress] = useState(true);
  const [isPreparingAddress, setIsPreparingAddress] = useState(false);
  // match after resolving missing street / number, so a second "Add new" doesn't create duplicates
  const [resolvedMatch, setResolvedMatch] = useState(null);
  // street / number created by this dialog; deleted again unless a realty gets created
  const createdAddressRef = useRef({});

  const lat = pin.lat.toFixed(6);
  const lng = pin.lng.toFixed(6);

  const { data: reverse = {}, isFetching: isAddressLoading } =
    useGetReverseAddressQuery({ token: userToken, lat, lng });
  const [pinRealty, { isLoading: isSaving }] = usePinRealtyMutation();
  const [addStreet] = useAddStreetMutation();
  const [addStreetNumber] = useAddStreetNumberMutation();
  const [deleteStreet] = useDeleteStreetMutation();
  const [deleteStreetNumber] = useDeleteStreetNumberMutation();
  const [fetchCityStreets] = streetsApi.useLazyGetStreetsForCityQuery();
  const [fetchStreetNumbers] =
    streetsNumberApi.useLazyGetStreetsNumberForStreetQuery();

  const suggestion = reverse.suggestion;
  const match = resolvedMatch ?? reverse.match;
  const missing = missingAddressParts(match, suggestion);

  const handleClose = () => {
    setPickedRealty(null);
    onClose();
  };

  const handleSave = async () => {
    try {
      await pinRealty({
        token: userToken,
        id: pickedRealty.id,
        latitude: pin.lat,
        longitude: pin.lng,
      }).unwrap();

      toast.success(`Location saved for #${pickedRealty.id}`);
      setPickedRealty(null);
      onSaved();
    } catch {
      toast.error('Could not save location');
    }
  };

  // the form stays open on "Save" and closes itself on "Save & close" (see handleAddClose)
  const handleRealtyCreated = async newId => {
    createdRealtyIdRef.current = newId;

    try {
      await pinRealty({
        token: userToken,
        id: newId,
        latitude: pin.lat,
        longitude: pin.lng,
      }).unwrap();
      toast.success(`Realty #${newId} placed on the map`);
    } catch {
      toast.error(
        `Realty #${newId} created, but its map location was not saved`
      );
    }
  };

  const handleAddNew = async () => {
    if (missing && shouldCreateAddress) {
      setIsPreparingAddress(true);
      const nextMatch = { ...match };

      try {
        // re-check against current data: reuse what exists, create only what doesn't
        if (missing.needsStreet) {
          const streets = await fetchCityStreets({
            token: userToken,
            city_id: nextMatch.city_id,
          }).unwrap();
          nextMatch.street_id = findIdByName(streets, suggestion.street);

          if (!nextMatch.street_id) {
            const street = await addStreet({
              token: userToken,
              data: { city_id: nextMatch.city_id, name: suggestion.street },
            }).unwrap();
            nextMatch.street_id = createdId(street);
            createdAddressRef.current.streetId = nextMatch.street_id;
          }
        }

        if (
          suggestion.housenumber &&
          nextMatch.street_id &&
          !nextMatch.street_number_id
        ) {
          const numbers = await fetchStreetNumbers({
            token: userToken,
            street_id: nextMatch.street_id,
          }).unwrap();
          nextMatch.street_number_id = findIdByName(
            numbers,
            suggestion.housenumber
          );

          if (!nextMatch.street_number_id) {
            // the number gets the BAN address point, the realty keeps its own pin
            const number = await addStreetNumber({
              token: userToken,
              data: {
                street_id: nextMatch.street_id,
                name: suggestion.housenumber,
                postal_code: suggestion.postcode || null,
                latitude: suggestion.latitude,
                longitude: suggestion.longitude,
              },
            }).unwrap();
            nextMatch.street_number_id = createdId(number);
            createdAddressRef.current.numberId = nextMatch.street_number_id;
          }
        }
      } catch {
        toast.error('Could not create the address — pick it in the form');
      } finally {
        setResolvedMatch(nextMatch);
        setIsPreparingAddress(false);
      }
    }

    setIsAddOpen(true);
  };

  // removes the street / number this dialog created when no realty ended up using them
  const discardCreatedAddress = async () => {
    const { streetId, numberId } = createdAddressRef.current;
    createdAddressRef.current = {};

    if (!isAdmin) return;

    // number first: it references the street
    if (numberId) await deleteStreetNumber({ token: userToken, id: numberId });
    if (streetId) await deleteStreet({ token: userToken, id: streetId });
  };

  // form closed: done if a realty was created, otherwise back to the pin dialog.
  // Ref, not state: on "Save & close" the form calls onCreated and closes in the same tick.
  const handleAddClose = () => {
    setIsAddOpen(false);

    if (createdRealtyIdRef.current) {
      onSaved();
      return;
    }

    discardCreatedAddress();
    setResolvedMatch(null);
  };

  // pin removed, moved (remounts this dialog) or linked to an existing realty instead
  const discardRef = useRef(discardCreatedAddress);
  discardRef.current = discardCreatedAddress;

  useEffect(
    () => () => {
      if (!createdRealtyIdRef.current) discardRef.current();
    },
    []
  );

  if (isAddOpen) {
    return (
      <Dialog
        content={
          <AddRealtyForm
            prefill={buildPrefill(match, suggestion)}
            mapPin={pin}
            onCreated={handleRealtyCreated}
            handleClose={handleAddClose}
          />
        }
        classes="w-full max-w-[768px]"
        isOpen={isAddOpen}
        onClose={handleAddClose}
      />
    );
  }

  const content = (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex gap-2 items-start">
        <MapPin
          size={18}
          className="shrink-0 mt-0.5 text-rose-600"
        />
        <div className="flex flex-col">
          <span className="text-sm font-semibold">
            {isAddressLoading
              ? 'Looking up address…'
              : suggestion
                ? formatAddress(suggestion)
                : 'No address found nearby'}
          </span>
          <span className="text-xs text-gray-500">
            {suggestion && !isAddressLoading
              ? `Nearest address · ${suggestion.distance_m} m from pin · `
              : ''}
            {lat}, {lng}
          </span>
        </div>
      </div>

      {pickedRealty ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm">
            Set this point as the map location of{' '}
            <span className="font-semibold">{realtyTitle(pickedRealty)}</span>?
          </p>
          <p className="text-xs text-gray-500">
            It replaces the realty&apos;s current map position.
          </p>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setPickedRealty(null)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-3 py-1.5 text-sm text-whiteColor bg-blackColor rounded hover:opacity-90 disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : 'Save location'}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Link to existing realty
          </span>
          <RealtyPinPicker onPick={setPickedRealty} />

          <div className="flex flex-col gap-2 pt-3 mt-1 border-t border-gray-200">
            <div className="flex items-center justify-between gap-2">
              <div className="flex flex-col">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Or add a new realty here
                </span>
                {!isAddressLoading && (
                  <span className="text-xs text-gray-500">
                    {suggestion
                      ? `Prefilled from address: ${matchSummary(match)}`
                      : 'Location fields are filled in by hand'}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={handleAddNew}
                disabled={isAddressLoading || isPreparingAddress}
                className="shrink-0 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
              >
                {isPreparingAddress ? 'Preparing…' : 'Add new realty'}
              </button>
            </div>

            {!isAddressLoading && missing && (
              <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={shouldCreateAddress}
                  onChange={e => setShouldCreateAddress(e.target.checked)}
                />
                <span>
                  Create &ldquo;{missing.label}&rdquo; in {suggestion.city} (
                  {[
                    missing.needsStreet && 'street',
                    missing.needsNumber && 'number',
                  ]
                    .filter(Boolean)
                    .join(' + ')}
                  )
                </span>
              </label>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={onRemovePin}
        className="self-start text-xs text-gray-500 underline hover:text-gray-800"
      >
        Remove pin
      </button>
    </div>
  );

  return (
    <Dialog
      content={content}
      isOpen={isOpen}
      onClose={handleClose}
      classes="max-w-[340px] sm:max-w-[420px]"
    />
  );
};
